import { Link } from "@tanstack/react-router";
import { ShieldCheck } from "lucide-react";
import { useState } from "react";
import { FlagKindPill, MetaText } from "@/components/plan-audit/AuditPrimitives";
import { EmptyState } from "@/components/shared/empty-state";
import { EnumSelect } from "@/components/shared/enum-select";
import { ErrorState } from "@/components/shared/error-state";
import { SearchInput } from "@/components/shared/search-input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { type RunFlag, useRun } from "@/hooks/use-plan-audit";
import { FLAG_CATEGORY_OPTIONS, FLAG_KIND_OPTIONS } from "@/lib/enums";

/**
 * Flags — every claim the plan makes that the intake does not support, shown
 * against the passage it was checked against.
 *
 * Plan text left, intake right, side by side. The comparison IS the finding: a
 * flag that just asserted "this is unsourced" would be an opinion, and one that
 * shows the two texts is evidence. A flag whose claim traces to nothing says so
 * loudly rather than rendering an empty panel, which would read as a loading bug.
 */
export function FlagsView({ reference }: { reference: string }) {
  const [search, setSearch] = useState("");
  const [kind, setKind] = useState("");
  const [category, setCategory] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);

  const {
    data: run,
    isLoading,
    isError,
    error,
    refetch,
  } = useRun(reference, {
    search: debouncedSearch,
    kind,
    category,
  });
  const [activeId, setActiveId] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="grid gap-4 lg:grid-cols-[340px_1fr]">
        <Skeleton className="h-96 rounded-xl" />
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }
  if (isError) return <ErrorState message={error.message} onRetry={() => void refetch()} />;
  if (!run) return <ErrorState message="Run not found" />;

  const flags = run.flags;
  // Selection follows the list rather than being held independently: filtering
  // can remove the selected flag, and a detail panel showing something no longer
  // in the list is worse than falling back to the first match.
  const active = flags.find((f) => f.id === activeId) ?? flags[0] ?? null;
  const filtering = debouncedSearch.length > 0 || kind.length > 0 || category.length > 0;

  return (
    <>
      <div className="pa-card mb-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-display text-lg font-bold text-[var(--brand-primary)]">
              {flags.length} flag{flags.length === 1 ? "" : "s"}
              {filtering && " matching"}
            </h2>
            <MetaText>
              {run.solution.name} · {run.planVersion.label} · run {run.reference} · checked against{" "}
              {run.planVersion.intakeSummary || "the intake"}
            </MetaText>
          </div>
          <Button asChild variant="outline">
            <Link to="/runs/$reference" params={{ reference: run.reference }}>
              Back to scorecard
            </Link>
          </Button>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <SearchInput
            value={search}
            onValueChange={setSearch}
            placeholder="Search flags"
            className="w-full sm:max-w-xs"
          />
          <EnumSelect
            value={kind}
            onValueChange={setKind}
            options={FLAG_KIND_OPTIONS}
            allOption={{ label: "All kinds" }}
            aria-label="Filter by flag kind"
            className="w-[180px]"
          />
          <EnumSelect
            value={category}
            onValueChange={setCategory}
            options={FLAG_CATEGORY_OPTIONS}
            allOption={{ label: "All patterns" }}
            aria-label="Filter by failure pattern"
            className="w-[240px]"
          />
        </div>
      </div>

      {flags.length === 0 ? (
        <div className="pa-card">
          <EmptyState
            icon={ShieldCheck}
            title={filtering ? "No flags match those filters" : "No flags raised"}
            description={
              filtering
                ? "Clear the filters to see every flag on this run."
                : "Every substantive claim in this plan traced to an intake passage."
            }
          />
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[340px_1fr] lg:items-start">
          <div className="pa-card pa-card-tight pa-scroll lg:max-h-[calc(100vh-19rem)]">
            {flags.map((flag) => (
              <button
                key={flag.id}
                type="button"
                className="pa-flag"
                data-active={flag.id === active?.id}
                onClick={() => setActiveId(flag.id)}
              >
                <div className="mb-1 flex items-center gap-2">
                  <span className="font-mono text-[11px] text-[var(--fg-3)]">{flag.reference}</span>
                  <FlagKindPill kind={flag.kind} />
                </div>
                <div className="text-[13px] leading-snug font-semibold text-[var(--fg-1)]">
                  {flag.title}
                </div>
                <MetaText>{flag.sectionName}</MetaText>
              </button>
            ))}
          </div>

          {active !== null && <FlagDetail flag={active} />}
        </div>
      )}
    </>
  );
}

function FlagDetail({ flag }: { flag: RunFlag }) {
  return (
    <div className="pa-card">
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <span className="font-mono text-xs text-[var(--fg-3)]">{flag.reference}</span>
        <FlagKindPill kind={flag.kind} />
        <MetaText>{flag.sectionName}</MetaText>
      </div>
      <h3 className="font-display text-lg leading-snug font-bold text-[var(--brand-primary)]">
        {flag.title}
      </h3>
      <p className="mt-2 text-[13px] leading-relaxed text-[var(--fg-2)]">{flag.why}</p>

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <div>
          <div className="pa-eyebrow mb-1">What the plan says</div>
          <MetaText>{flag.planReference}</MetaText>
          <blockquote className="pa-quote mt-1.5">{flag.planText}</blockquote>
        </div>
        <div>
          <div className="pa-eyebrow mb-1">What the intake says</div>
          <MetaText>
            {flag.intakeTraced ? (
              flag.intakeReference
            ) : (
              // The absence of a source is the finding. It gets the red
              // treatment, not a blank panel that reads like a failed fetch.
              <span style={{ color: "var(--danger-fg)", fontWeight: 600 }}>
                No traceable passage
              </span>
            )}
          </MetaText>
          <blockquote className={`pa-quote mt-1.5 ${flag.intakeTraced ? "" : "pa-quote-untraced"}`}>
            {flag.intakeText}
          </blockquote>
        </div>
      </div>

      {flag.effect.length > 0 && (
        <div
          className="mt-4 rounded-lg border p-3"
          style={{ background: "var(--bg-2)", borderColor: "var(--border-1)" }}
        >
          <div className="pa-eyebrow mb-1">Effect on the score</div>
          <p className="text-xs leading-relaxed text-[var(--fg-2)]">{flag.effect}</p>
        </div>
      )}
    </div>
  );
}
